/**
 * BRAND EVENTS SECTION
 * 
 * Displays upcoming events for a brand on their brand page.
 * Shows next 5 upcoming events with links to the full calendar.
 * 
 * Location: src/components/brands/BrandEventsSection.tsx
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Tag, ChevronRight, Bell } from 'lucide-react';
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import type { CalendarEvent, EventType } from '../../types/event';
import { EVENT_TYPE_CONFIG } from '../../types/event';

interface BrandEventsSectionProps {
  brandId: string;
  brandSlug: string;
  brandName: string;
  isFollowing?: boolean;
}

export default function BrandEventsSection({ 
  brandId, 
  brandSlug, 
  brandName,
  isFollowing = false 
}: BrandEventsSectionProps) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // Load upcoming events for this brand
  useEffect(() => {
    async function loadEvents() {
      try {
        setLoading(true);
        // Use start of today so events that started today still show
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const eventsRef = collection(db, 'events');
        // Query events where endDate >= start of today (so ongoing events show)
        const q = query(
          eventsRef,
          where('brandId', '==', brandId),
          where('isPublished', '==', true),
          where('endDate', '>=', Timestamp.fromDate(today)),
          orderBy('endDate', 'asc'),
          limit(5)
        );
        
        const snapshot = await getDocs(q);
        const eventsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as CalendarEvent[];
        
        setEvents(eventsData);
      } catch (err) {
        console.error('Error loading brand events:', err);
      } finally {
        setLoading(false);
      }
    }
    
    loadEvents();
  }, [brandId]);

  // Don't render if no events and not loading
  if (!loading && events.length === 0) {
    return null;
  }

  // Format date for display
  const formatEventDate = (event: CalendarEvent): string => {
    const date = event.startDate.toDate();
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  };

  // Format time for display
  const formatEventTime = (event: CalendarEvent): string | null => {
    if (event.isAllDay) return null;
    if (!event.startTime) return null;
    
    const [hours, minutes] = event.startTime.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    
    let timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
    
    // Add timezone abbreviation if available
    if (event.timezone) {
      const tzAbbr = event.timezone.split('/').pop()?.replace('_', ' ');
      if (tzAbbr) {
        // Get short timezone like "PT" from "Los_Angeles"
        const shortTz = {
          'New_York': 'ET',
          'Chicago': 'CT', 
          'Denver': 'MT',
          'Los_Angeles': 'PT',
          'Phoenix': 'AZ',
          'Anchorage': 'AKT',
          'Honolulu': 'HT'
        }[tzAbbr] || tzAbbr;
        timeStr += ` ${shortTz}`;
      }
    }
    
    return timeStr;
  };

  // Get event type info
  const getTypeInfo = (type: EventType) => {
    return EVENT_TYPE_CONFIG[type] || EVENT_TYPE_CONFIG.release;
  };

  return (
    <div className="glass-card p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-glass flex items-center gap-2">
          <Calendar className="w-5 h-5 text-pink-400" />
          Upcoming Events
        </h2>
        <Link
          to={`/events?brand=${brandSlug}`}
          className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
        >
          View All
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-pink-400 border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(event => {
            const typeInfo = getTypeInfo(event.eventType);
            const dateStr = formatEventDate(event);
            const timeStr = formatEventTime(event);
            
            return (
              <Link
                key={event.id}
                to={`/events?highlight=${event.id}`}
                className="flex items-start gap-4 p-3 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 transition-colors group"
              >
                {/* Date badge */}
                <div className="flex-shrink-0 w-14 text-center">
                  <div className="text-xs text-glass-muted uppercase">
                    {dateStr.split(',')[0]}
                  </div>
                  <div className="text-2xl font-bold text-glass">
                    {event.startDate.toDate().getDate()}
                  </div>
                  <div className="text-xs text-glass-muted">
                    {event.startDate.toDate().toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                </div>
                
                {/* Event details */}
                <div className="flex-1 min-w-0">
                  {/* Type badge */}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${typeInfo.color} text-white mb-1`}>
                    {typeInfo.emoji} {typeInfo.label}
                  </span>
                  
                  {/* Title */}
                  <h3 className="font-medium text-glass group-hover:text-pink-300 transition-colors truncate">
                    {event.title}
                  </h3>
                  
                  {/* Time */}
                  {timeStr && (
                    <p className="text-sm text-glass-muted flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {timeStr}
                    </p>
                  )}
                  
                  {/* Discount info */}
                  {event.discountInfo && (
                    <p className="text-sm text-green-400 flex items-center gap-1 mt-1">
                      <Tag className="w-3 h-3" />
                      {event.discountInfo}
                    </p>
                  )}
                </div>
                
                {/* Shop link */}
                {event.shopUrl && (
                  <a
                    href={event.shopUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0 px-3 py-1.5 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-lg text-sm transition-colors"
                  >
                    Shop
                  </a>
                )}
              </Link>
            );
          })}
          
          {/* Follow prompt if not following */}
          {!isFollowing && events.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-purple-500/10 rounded-lg text-sm">
              <Bell className="w-4 h-4 text-purple-400" />
              <span className="text-glass-secondary">
                Follow {brandName} to get notified about new events!
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
